/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import classNames from 'classnames';
import { Button, Spinner } from 'react-bootstrap';

import type { VideoPublicItem } from '@/common/interface';
import { useVideoReferenceCandidates } from '@/services';

import Icon from '../Icon';

import './index.scss';

interface Props {
  questionId: string;
  value: VideoPublicItem[];
  onChange: (items: VideoPublicItem[]) => void;
  disabled?: boolean;
}

const copy = {
  title: '\u5f15\u7528\u5df2\u6709\u89c6\u9891',
  summary:
    '\u53ef\u5f15\u7528\u81ea\u5df1\u53d1\u8fc7\u7684\u89c6\u9891\uff0c\u4e5f\u53ef\u5f15\u7528\u6309\u6807\u7b7e\u5339\u914d\u7684\u4ed6\u4eba\u89c6\u9891\u3002\u4ed6\u4eba\u89c6\u9891\u88ab\u89e3\u9501\u540e\uff0c\u4f5c\u8005\u548c\u63a8\u8350\u4eba 5:5 \u5206\u6210\u3002',
  mine: '\u6211\u7684\u89c6\u9891',
  recommended: '\u6807\u7b7e\u5339\u914d\u63a8\u8350',
  empty: '\u6682\u65e0\u53ef\u5f15\u7528\u89c6\u9891',
  selected: '\u5df2\u9009',
  select: '\u5f15\u7528',
  coin: '\u521b\u4f5c\u5e01',
};

const normalizeItems = (items?: VideoPublicItem[]) =>
  Array.isArray(items) ? items.filter((item) => item?.id) : [];

const VideoReferencePicker = ({
  questionId,
  value,
  onChange,
  disabled = false,
}: Props) => {
  const { data, isLoading } = useVideoReferenceCandidates(
    questionId ? { question_id: questionId, page_size: 8 } : undefined,
  );
  const selectedIDs = new Set(value.map((item) => item.id));
  const mine = normalizeItems(data?.mine);
  const recommended = normalizeItems(data?.recommended).filter(
    (item) => !mine.some((own) => own.id === item.id),
  );
  const hasAny = mine.length > 0 || recommended.length > 0;

  const toggle = (item: VideoPublicItem) => {
    if (disabled) {
      return;
    }
    if (selectedIDs.has(item.id)) {
      onChange(value.filter((selected) => selected.id !== item.id));
      return;
    }
    onChange([...value, item]);
  };

  const renderList = (items: VideoPublicItem[]) => {
    if (items.length === 0) {
      return <div className="video-reference-picker-empty">{copy.empty}</div>;
    }
    return (
      <div className="video-reference-picker-list">
        {items.map((item) => {
          const selected = selectedIDs.has(item.id);
          return (
            <button
              key={item.id}
              type="button"
              className={classNames('video-reference-picker-item', {
                'is-selected': selected,
              })}
              disabled={disabled}
              onClick={() => toggle(item)}>
              <span
                className={classNames('video-reference-picker-cover', {
                  'has-cover': Boolean(item.cover_url),
                })}
                style={
                  item.cover_url
                    ? { backgroundImage: `url(${item.cover_url})` }
                    : undefined
                }>
                <Icon name="play-fill" />
              </span>
              <span className="video-reference-picker-info">
                <strong>{item.title || item.question_title}</strong>
                <small>
                  {item.price_amount || 1} {copy.coin}
                </small>
              </span>
              <Button
                as="span"
                size="sm"
                variant={selected ? 'primary' : 'outline-primary'}>
                {selected ? copy.selected : copy.select}
              </Button>
            </button>
          );
        })}
      </div>
    );
  };

  if (!questionId) {
    return null;
  }

  return (
    <section className="video-reference-picker">
      <div className="video-reference-picker-head">
        <div>
          <h6>{copy.title}</h6>
          <p>{copy.summary}</p>
        </div>
        <Icon name="collection-play" />
      </div>
      {isLoading ? (
        <div className="video-reference-picker-loading">
          <Spinner animation="border" size="sm" />
        </div>
      ) : (
        <>
          {hasAny && mine.length > 0 && (
            <div className="video-reference-picker-section">
              <div className="video-reference-picker-section-title">
                {copy.mine}
              </div>
              {renderList(mine)}
            </div>
          )}
          {hasAny && recommended.length > 0 && (
            <div className="video-reference-picker-section">
              <div className="video-reference-picker-section-title">
                {copy.recommended}
              </div>
              {renderList(recommended)}
            </div>
          )}
          {!hasAny && renderList([])}
        </>
      )}
    </section>
  );
};

export default VideoReferencePicker;
